import { z } from 'zod';
/**
 * Durable event envelope written through each service's transactional outbox. Consumers are
 * idempotent on `id`, tolerate duplicates and out-of-order delivery, and switch on `type` + `version`.
 * The service that emits an event type owns its schema.
 */
export const eventEnvelopeSchema = z.object({
    id: z.uuid(),
    type: z.string().regex(/^[a-z]+(\.[a-z_]+)+$/),
    version: z.number().int().positive(),
    source: z.string(),
    occurredAt: z.iso.datetime(),
    organizationId: z.uuid().nullable(),
    correlationId: z.string().nullable(),
    actor: z
        .object({ type: z.enum(['user', 'staff', 'system', 'service']), id: z.string().nullable() })
        .nullable(),
    data: z.record(z.string(), z.unknown()),
});
/** Event types platform-api emits (version 1). */
export const PLATFORM_EVENT_TYPES = [
    'platform.organization.created',
    'platform.organization.updated',
    'platform.organization.suspended',
    'platform.membership.created',
    'platform.membership.updated',
    'platform.membership.removed',
    'platform.entitlement.changed',
    'platform.module_config.changed',
    'platform.domain.verified',
    'platform.domain.released',
    'platform.access.revoked',
    'platform.invitation.created',
    'platform.invitation.accepted',
    // 0.7.0 (milestone 2): the changes Trace Admin makes, so consumers drop what they cached.
    'platform.invitation.revoked',
    'platform.module.emergency_stopped',
    'platform.module.emergency_released',
    'platform.release_flag.changed',
    'platform.domain.created',
    'platform.domain.disabled',
    'platform.domain.enabled',
    'platform.support_access.granted',
    'platform.support_access.revoked',
    // 0.8.0 (milestone 2b): organizations add, verify and remove their own email domains (sign-in discovery).
    'platform.email_domain.added',
    'platform.email_domain.verified',
    'platform.email_domain.removed',
    // 0.9.0 (milestone 3): billing (BILLING_EVENT_TYPES in billing.ts names the same list).
    'platform.billing.plan_version_drafted',
    'platform.billing.plan_version_published',
    'platform.billing.contract_created',
    'platform.billing.contract_changed',
    'platform.billing.adjustment_requested',
    'platform.billing.adjustment_approved',
    'platform.billing.adjustment_rejected',
    'platform.billing.spend_cap_reached',
    'platform.billing.period_rated',
    'platform.billing.document_updated',
    'platform.billing.collection_stopped',
    // Unreleased (additive): a card-required trial lapsed without a card (design section 6 item 14).
    'platform.billing.trial_lapsed',
    // Unreleased (additive): a trial used its token budget; a period passed the self-serve shipments.
    'platform.billing.trial_budget_used',
    'platform.billing.self_serve_exceeded',
    // Unreleased (additive): staff ended a contract's trial early.
    'platform.billing.trial_ended',
    // Unreleased (additive): staff set or cleared an organization's billing exemption (internal organizations).
    'platform.billing.exemption_changed',
    // Unreleased (additive): Trace Admin linked or synced an organization's Auth0 organization.
    'platform.organization.identity_provider_linked',
    // Unreleased (additive): Trace staff added or ended an organization's product subscription.
    'platform.subscription.changed',
    // Unreleased (additive): a platform admin added, changed, suspended or revoked a Trace staff member.
    'platform.staff.changed',
    // Unreleased (additive): Trace staff created or changed a portal or a portal customer account.
    'platform.portal.changed',
    'platform.customer_account.changed',
    // 0.18.0 (additive): a ticket was filed, escalated to Trace, or got a message.
    'platform.ticket.created',
    'platform.ticket.escalated',
    'platform.ticket.message_added',
    // 0.18.0 (additive, same branch): what the customer sees of a ticket changed through Trace (a status, a hand back,
    // a duplicate), with the customer status before and after.
    'platform.ticket.status_changed',
    // 0.20.0 (self-serve sign-up): SELF_SERVE_EVENT_TYPES in self-serve.ts names the same list.
    'platform.self_serve.signup_created',
    'platform.self_serve.card_saved',
    'platform.self_serve.signup_completed',
    'platform.self_serve.signup_needs_attention',
    // 0.21.0 (additive): Trace staff turned self-serve sign-up on or off (SELF_SERVE_EVENT_TYPES names it too).
    'platform.self_serve.switch_changed',
];
//# sourceMappingURL=events.js.map